/**
 * React-query hooks over `sdk.context` — the source registry and the per-user
 * profile the agent reads every turn.
 *
 * MUTATIONS SEED THE CACHE FROM THE RESPONSE; THEY DO NOT INVALIDATE.
 * Every mutation here runs a Temporal workflow, and an immediate
 * invalidate→refetch races that write's visibility across PgCat connection
 * pooling — the refetch can return a list still missing the row that was just
 * written. The mutation response already carries the authoritative post-write
 * state, so it is written straight into the cache. Same fix and same reasoning
 * as the provider-credential seed in `useProviderManagement`. Do not
 * "simplify" these back to invalidate.
 */
import { useEffect, useRef } from 'react';
import {
  useQuery,
  useMutation,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { getSDK } from '../services/sdk-instance';
import { holdsIdentityAtom } from '../stores/auth/atoms';
import { bridgeEventStreamAtom, sdkConfiguredAtom } from '../stores/sdk/atoms';
import { useShogunchop } from '../providers/ShogunChopProvider';
import {
  WORKSPACE_CONTEXT_OBSERVED,
  type ContextSource,
  type CreateContextSourceRequest,
  type ListContextSourcesHealthResponse,
  type ListContextSourcesResponse,
  type UpdateContextSourceRequest,
  type UserProfile,
  type UserProfilePatch,
  type UserProfileView,
  type WorkspaceContext,
  type WorkspaceContextObserved,
} from '@localagent/sdk';

export const PROFILE_KEY = ['user-profile'] as const;
export const HEALTH_KEY = ['context-sources-health'] as const;
export const CONTEXT_SOURCES_KEY = ['context-sources'] as const;
export const WORKSPACE_CONTEXT_KEY = ['workspace-context'] as const;

function sourcesKey(workspaceId?: string): readonly string[] {
  return workspaceId ? [...CONTEXT_SOURCES_KEY, workspaceId] : CONTEXT_SOURCES_KEY;
}

function setSourceList(
  queryClient: QueryClient,
  workspaceId: string | undefined,
  update: (prev: ContextSource[]) => ContextSource[],
): void {
  queryClient.setQueryData<ListContextSourcesResponse>(
    sourcesKey(workspaceId),
    (prev) => ({ sources: update(prev?.sources ?? []) }),
  );
}

/**
 * Insert-or-replace `row` by id, then restore the server's list order.
 *
 * The server sorts `ORDER BY ordering, scope, name` — `ordering` is dense
 * PER SCOPE BUCKET, so rows from different scopes legitimately share an
 * ordering value and the scope/name tiebreak is what makes the sequence total.
 * Sorting on `ordering` alone would leave same-ordering rows in
 * whatever order the array happened to hold, which is exactly the undefined
 * relative order a total precedence forbids.
 */
function upsertInServerOrder(
  list: ContextSource[],
  row: ContextSource,
): ContextSource[] {
  const next = list.filter((s) => s.id !== row.id);
  next.push(row);
  next.sort(
    (a, b) =>
      a.ordering - b.ordering ||
      a.scope.localeCompare(b.scope) ||
      a.name.localeCompare(b.name),
  );
  return next;
}

export function useContextSources(workspaceId?: string) {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const sdkConfigured = useAtomValue(sdkConfiguredAtom);

  return useQuery<ListContextSourcesResponse, Error>({
    queryKey: sourcesKey(workspaceId),
    queryFn: () => getSDK().context.sources.list(workspaceId),
    enabled: Boolean(isAuthenticated && sdkConfigured),
  });
}

// Read on mount; re-read only when workspace.contextObserved carries a new fingerprint (context ADR-26077).
export function useWorkspaceContext(workspaceId: string) {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const sdkConfigured = useAtomValue(sdkConfiguredAtom);
  const bridgeStream = useAtomValue(bridgeEventStreamAtom);
  const shogunchop = useShogunchop();

  const query = useQuery<WorkspaceContext, Error>({
    queryKey: [...WORKSPACE_CONTEXT_KEY, workspaceId],
    queryFn: () => getSDK().context.workspace.get(workspaceId),
    enabled: Boolean(isAuthenticated && sdkConfigured),
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
    retry: false,
  });

  const { data, isFetching, refetch } = query;
  const shownFingerprint = useRef<string | undefined>(undefined);
  shownFingerprint.current = data?.fingerprint;
  const fetching = useRef(false);
  fetching.current = isFetching;
  const observedFingerprint = useRef<string | null>(null);
  const requestedFingerprint = useRef<string | null>(null);

  const reconcile = useRef(() => {});
  reconcile.current = () => {
    const observed = observedFingerprint.current;
    const shown = shownFingerprint.current;
    if (observed === null || shown === undefined || fetching.current) return;
    if (observed === shown || observed === requestedFingerprint.current) return;
    requestedFingerprint.current = observed;
    void refetch({ cancelRefetch: false });
  };

  useEffect(() => {
    reconcile.current();
  }, [data, isFetching]);

  useEffect(() => {
    observedFingerprint.current = null;
    requestedFingerprint.current = null;
    const observe = (observed: WorkspaceContextObserved) => {
      if (observed.workspace_id !== workspaceId) return;
      observedFingerprint.current = observed.fingerprint;
      reconcile.current();
    };
    const daemon = shogunchop.resource;
    if (daemon) {
      const relay = daemon.sharedEvents();
      const off = relay.on((event) => {
        if (event.event === WORKSPACE_CONTEXT_OBSERVED) observe(event.data);
      });
      return () => {
        off();
        relay.release();
      };
    }
    if (!bridgeStream) return;
    return bridgeStream.onWorkspaceContextObserved(observe);
  }, [bridgeStream, shogunchop.resource, workspaceId]);

  return query;
}

/**
 * Account-scoped latest per-source health.
 * Sparse: never-observed sources are ABSENT from the response.
 */
export function useContextSourceHealth() {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const sdkConfigured = useAtomValue(sdkConfiguredAtom);

  return useQuery<ListContextSourcesHealthResponse, Error>({
    queryKey: HEALTH_KEY,
    queryFn: () => getSDK().context.sources.health(),
    enabled: Boolean(isAuthenticated && sdkConfigured),
  });
}

export function useCreateContextSource(workspaceId?: string) {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const queryClient = useQueryClient();

  return useMutation<ContextSource, Error, CreateContextSourceRequest>({
    mutationFn: (req) => getSDK().context.sources.create(req),
    onSuccess: (created) => {
      if (isAuthenticated) {
        setSourceList(queryClient, workspaceId, (prev) =>
          upsertInServerOrder(prev, created),
        );
      }
    },
  });
}

export function useUpdateContextSource(workspaceId?: string) {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const queryClient = useQueryClient();

  return useMutation<
    ContextSource,
    Error,
    { sourceId: string; req: UpdateContextSourceRequest }
  >({
    mutationFn: ({ sourceId, req }) =>
      getSDK().context.sources.update(sourceId, req),
    onSuccess: (updated) => {
      if (isAuthenticated) {
        setSourceList(queryClient, workspaceId, (prev) =>
          upsertInServerOrder(prev, updated),
        );
      }
    },
  });
}

export function useDeleteContextSource(workspaceId?: string) {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const queryClient = useQueryClient();

  return useMutation<{ deleted: boolean }, Error, string>({
    mutationFn: (sourceId) => getSDK().context.sources.delete(sourceId),
    onSuccess: (_result, sourceId) => {
      if (isAuthenticated) {
        setSourceList(queryClient, workspaceId, (prev) =>
          prev.filter((s) => s.id !== sourceId),
        );
      }
    },
  });
}

export function useMoveContextSource(workspaceId?: string) {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const queryClient = useQueryClient();

  return useMutation<
    ListContextSourcesResponse,
    Error,
    { sourceId: string; position: number }
  >({
    mutationFn: ({ sourceId, position }) =>
      getSDK().context.sources.move(sourceId, {
        position,
        ...(workspaceId ? { workspace_id: workspaceId } : {}),
      }),
    onSuccess: (list) => {
      if (isAuthenticated) {
        queryClient.setQueryData<ListContextSourcesResponse>(
          sourcesKey(workspaceId),
          list,
        );
      }
    },
  });
}

export function useUserProfile() {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const sdkConfigured = useAtomValue(sdkConfiguredAtom);

  return useQuery<UserProfileView, Error>({
    queryKey: PROFILE_KEY,
    queryFn: () => getSDK().context.profile.get(),
    enabled: Boolean(isAuthenticated && sdkConfigured),
  });
}

export function useUpdateUserProfile() {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const queryClient = useQueryClient();

  return useMutation<UserProfile, Error, UserProfilePatch>({
    mutationFn: (patch) => getSDK().context.profile.update(patch),
    onSuccess: (saved) => {
      if (!isAuthenticated) return;
      queryClient.setQueryData<UserProfileView>(PROFILE_KEY, (prev) =>
        prev ? { ...prev, profile: saved } : undefined,
      );
      void queryClient.refetchQueries({ queryKey: PROFILE_KEY });
    },
  });
}
